import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const AriaBWV515Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Aria BWV 515
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV515/anna-magdalena-20a/anna-magdalena-20a-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Aria BWV 515.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-2928">
      <Navbar data-id="element-2929" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-2930">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-2931">
          <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop" alt="Aria BWV 515" className="w-full h-64 md:h-80 object-cover" data-id="element-2932" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-2933" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-2934">
            <div className="max-w-7xl mx-auto" data-id="element-2935">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-2936">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-2937" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-2938">
                Aria
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-2939">
                BWV 515, Anna Magdalena Notebook
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-2940">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-2941">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-2942">
          <div className="flex items-center space-x-2" data-id="element-2943">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-2944">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-2945" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2946">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-2947">
                  <ZoomInIcon className="h-5 w-5" data-id="element-2948" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-2949">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-2950">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-2951" />
              <span className="hidden sm:inline" data-id="element-2952">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-2953">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-2954" />
              <span className="hidden sm:inline" data-id="element-2955">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-2956">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-2957">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-2958">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Aria BWV 515 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-2959" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-2960">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2961">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-2962">
            <div data-id="element-2963">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2964">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-2965">
                <div data-id="element-2966">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2967">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2968">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-2969">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2970">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2971">
                    c. 1722-1725
                  </dd>
                </div>
                <div data-id="element-2972">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2973">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2974">
                    Aria
                  </dd>
                </div>
                <div data-id="element-2975">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2976">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2977">
                    BWV 515
                  </dd>
                </div>
                <div data-id="element-2978">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2979">
                    Source
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2980">
                    Anna Magdalena Notebook
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-2981">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-2982">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2983">
                This charming aria comes from the Notebook for Anna Magdalena
                Bach, a collection compiled by Bach for his second wife. The
                notebook contains a mixture of keyboard pieces, songs, and
                chorales, many intended for domestic music-making and teaching.
                This aria exemplifies the elegant, lyrical style characteristic
                of the notebook's contents. With its graceful melodic lines and
                gentle harmonic progressions, the piece offers an intimate
                glimpse into the Bach household's musical life. The aria's
                simplicity and expressiveness make it an excellent introduction
                to Bach's keyboard writing, while still containing the
                sophisticated voice leading and harmonic richness that define
                his compositional style.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-2984" />
    </div>;
};